'use client';

import type { UIEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Image } from '@/app/components/media/image';
import { getMediaCondition } from '@/app/components/media/image/utils';
import type { VideoMediaType, VideoSource, VideoSrcType } from '@/app/components/media/video/types';
import { TAILWIND_BREAKPOINTS_SIZE } from '@/app/constants/styles/breakpoints';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
import { playVideoWithPromiseHandle } from '@/app/utils/video';

/**
 * Properties for the Video component.
 */
export interface VideoProps {
  /** <b>IMPORTANT</b> - Information for the responsive video rendering  */
  sources: VideoSource;

  /** <i>Optional</i> - flag to indicate if the video should be loaded with high priority. If true, the video will be loaded with high priority. */
  priority?: boolean;

  /** <i>Optional</i> - boundary margin for lazy loading the video. Specifies the margin around the viewport to start loading the video. */
  rootMargin?: string;

  /** <i>Optional</i> - callback function that is called when the video is ready to play. The event object is passed as an argument. */
  onImageLoaded?: () => void;

  /** <i>Optional</i> - callback function that is called when the video is ready to play. The event object is passed as an argument. */
  onReady?: (e?: UIEvent<HTMLVideoElement>) => void;

  /** <i>Optional</i> - callback function that is called when the video start playing. The event object is passed as an argument. */
  onVideoPlay?: (e: UIEvent<HTMLVideoElement>) => void;

  /** <i>Optional</i> - callback function that is called when the video has finished playing. The event object is passed as an argument. */
  onVideoEnded?: (e: UIEvent<HTMLVideoElement>) => void;

  /** <i>Optional</i> - callback with currentTime and duration (seconds) for progress tracking */
  onProgress?: (currentTime: number, duration: number) => void;

  /** <i>Optional</i> - flag to prevent the video from loading automatically. If true, the video will not load automatically. */
  preventLoadVideo?: boolean;

  /** <i>Optional</i> - flag to indicate if the video should loop when ended. If true, the video will loop when it ends. */
  loop?: boolean;

  /** <i>Optional</i> - flag to indicate if the video should play automatically. Default is true */
  autoplay?: boolean;

  /** <i>Optional</i> - flag to indicate if the video should be paused. If true, the video will be paused. */
  paused?: boolean;

  /** <i>Optional</i> - when pausing, seek back to the start (e.g. carousel slides). */
  rewindWhenPaused?: boolean;

  /** <i>Optional</i> - Override the video wrapper div style */
  className?: string;
}
export const Video = ({
  priority,
  rootMargin,
  sources,
  preventLoadVideo,
  autoplay = false,
  loop = false,
  paused = false,
  rewindWhenPaused = false,
  className,
  onReady,
  onVideoPlay,
  onVideoEnded,
  onProgress,
  onImageLoaded,
  ...rest
}: VideoProps) => {
  const [currentMediaDevice, setCurrentMediaDevice] = useState<VideoMediaType | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = sources.video?.mobile && sources.video?.desktop;
  const videoSrc = preventLoadVideo || !currentMediaDevice ? undefined : sources.video[currentMediaDevice];
  const handleLoadedMetadata = (e: UIEvent<HTMLVideoElement>) => {
    if (!videoRef.current) return;
    if (!autoplay || paused) {
      videoRef.current.pause();
      if (rewindWhenPaused) {
        videoRef.current.currentTime = 0;
      }
    }
    safeCall(onReady, e);
  };
  const handleVideoPlay = (e: UIEvent<HTMLVideoElement>) => {
    safeCall(onVideoPlay, e);
  };
  const handleVideoEnded = (e: UIEvent<HTMLVideoElement>) => {
    if (videoRef.current?.ended) {
      safeCall(onVideoEnded, e);
    }
  };
  const handleTimeUpdate = (e: UIEvent<HTMLVideoElement>) => {
    const el = e.currentTarget;
    if (el.duration && isFinite(el.duration)) {
      safeCall(onProgress, el.currentTime, el.duration);
    }
  };
  const getWindowSizeMediaDevice = (): VideoMediaType => {
    const desktopSize = sources.desktopBreakpoint ? TAILWIND_BREAKPOINTS_SIZE[sources.desktopBreakpoint] : TAILWIND_BREAKPOINTS_SIZE.xl;
    const width = window.innerWidth;
    return width >= desktopSize ? 'desktop' : 'mobile';
  };
  const handleImageLoad = (el?: HTMLImageElement) => {
    const imgSrc = el?.currentSrc;
    if (!hasVideo || !imgSrc) return safeCall(onImageLoaded);
    const foundedDevice = sources.responsives.find(item => window.matchMedia(getMediaCondition(item)).matches);
    const mediaDevice = foundedDevice?.media?.device as keyof VideoSrcType;
    if (!!mediaDevice) {
      setCurrentMediaDevice(mediaDevice);
    } else {
      const device = getWindowSizeMediaDevice();
      setCurrentMediaDevice(device);
    }
    safeCall(onImageLoaded);
  };
  useEffect(() => {
    if (hasVideo) return;
    safeCall(onReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (paused) {
      if (!el.paused) {
        el.pause();
      }
      if (rewindWhenPaused) {
        el.currentTime = 0;
      }
    } else if (el.paused) {
      playVideoWithPromiseHandle(el);
    }
  }, [paused, rewindWhenPaused]);
  return <div {...rest} className={cn('absolute inset-0 w-full', className)}>
      <Image priority={priority} rootMargin={rootMargin} src={sources.img.src} alt={sources.img.alt} width={sources.img.width} height={sources.img.height} layout="fill" sources={sources.responsives} onLoad={handleImageLoad} />
      {!!hasVideo && <video ref={videoRef} preload="none" autoPlay muted playsInline className="absolute inset-0 h-0 max-h-fit min-h-full w-0 max-w-full min-w-full object-cover object-center" src={videoSrc} onLoadedMetadata={handleLoadedMetadata} onPlay={handleVideoPlay} onTimeUpdate={onProgress ? handleTimeUpdate : undefined} onEnded={onVideoEnded ? handleVideoEnded : undefined} loop={loop} {...createDataAttributes({
      test: 'video'
    })} />}
    </div>;
};